import React, { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { withAuthRefresh } from '../../utils/auth';
import { 
  Container, 
  Typography, 
  Box, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  TextField, 
  Button, 
  Tabs, 
  Tab, 
  Skeleton, 
  Alert, 
  Chip, 
  Link, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions,
  Card,
  CardContent,
  Grid,
  Paper,
  Fade,
  LinearProgress
} from '@mui/material';
import { 
  Assignment as AssignmentIcon,
  Timer as TimerIcon,
  Send as SendIcon,
  Description as DescriptionIcon,
  LocalLibrary as LibraryIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import OrderedList from '@tiptap/extension-ordered-list';
import Blockquote from '@tiptap/extension-blockquote';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { Link as RouterLink } from 'react-router-dom';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { v4 as uuidv4 } from 'uuid';

// Vite/webpack5+ 환경에서 ESM 경로를 문자열로 변환
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error("Supabase URL or Anon Key is missing. Check your .env file.");
}

const PdfViewer = ({ url, label }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const containerRef = useRef(null);
  const [pdfWidth, setPdfWidth] = useState(800);

  useEffect(() => {
    const updatePdfSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setPdfWidth(Math.min(containerWidth - 16, 1200)); // 최대 1200px, 좌우 여백 고려
      }
    };
    updatePdfSize();
    window.addEventListener('resize', updatePdfSize);
    return () => window.removeEventListener('resize', updatePdfSize);
  }, []);

  useEffect(() => {
    setPageNumber(1);
    setNumPages(null);
  }, [url]);

  return (
    <Box 
      ref={containerRef} 
      sx={{ 
        width: '100%', 
        height: '100%', 
        minHeight: 0, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'stretch' 
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 2, pt: 2 }}>
        <DescriptionIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
        <Typography variant="h6" fontWeight="bold" color="primary.main">
          {label}
        </Typography>
      </Box>
      {url ? (
        <>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
            <Document file={url} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
              <Page pageNumber={pageNumber} width={pdfWidth} />
            </Document>
          </Box>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: 2, 
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'rgba(0,0,0,0.02)',
              flexShrink: 0
            }}
          >
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => setPageNumber(p => Math.max(1, p - 1))} 
              disabled={pageNumber <= 1}
              sx={{ borderRadius: 2 }}
            >
              이전
            </Button>
            <Typography fontWeight="bold" color="primary.main">
              {pageNumber} / {numPages || '?'}
            </Typography>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => setPageNumber(p => Math.min(numPages || 1, p + 1))} 
              disabled={!numPages || pageNumber >= numPages}
              sx={{ borderRadius: 2 }}
            >
              다음
            </Button>
          </Box>
        </>
      ) : (
        <Box sx={{ 
          flex: 1,
          height: 400, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          color: 'text.secondary',
          flexDirection: 'column',
          gap: 2
        }}>
          <DescriptionIcon sx={{ fontSize: 64, opacity: 0.3 }} />
          <Typography variant="h6" color="text.secondary">
            시험 세트를 선택하세요
          </Typography>
        </Box>
      )}
    </Box>
  );
};

function formatTime(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

const TIMER_DURATION = 90 * 60; // 1시간 30분 = 5400초

const RichTextEditor = ({ value, onChange, disabled, placeholder }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      OrderedList,
      Blockquote,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
      },
    },
  });

  // 에디터 내용 동기화
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  // placeholder 설정
  useEffect(() => {
    if (editor && !editor.getText() && placeholder) {
      editor.commands.setContent(`<p class="is-editor-empty" data-placeholder="${placeholder}"></p>`);
    }
  }, [editor, placeholder]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  if (!editor) {
    return null;
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      flex: 1,
      overflow: 'hidden',
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider'
    }}>
      <Box sx={{ 
        display: 'flex', 
        gap: 0.5, 
        p: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        flexShrink: 0,
      }}>
        <Button 
          size="small" 
          variant={editor.isActive('heading', { level: 1 }) ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          sx={{ minWidth: 40, px: 1, borderRadius: 1 }}
          disabled={disabled}
        >
          H1
        </Button>
        <Button 
          size="small" 
          variant={editor.isActive('heading', { level: 2 }) ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          sx={{ minWidth: 40, px: 1, borderRadius: 1 }}
          disabled={disabled}
        >
          H2
        </Button>
        <Button 
          size="small" 
          variant={editor.isActive('heading', { level: 3 }) ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          sx={{ minWidth: 40, px: 1, borderRadius: 1 }}
          disabled={disabled}
        >
          H3
        </Button>
        <Button 
          size="small" 
          variant={editor.isActive('bulletList') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          sx={{ minWidth: 40, px: 1, borderRadius: 1 }}
          disabled={disabled}
        >
          •
        </Button>
        <Button 
          size="small" 
          variant={editor.isActive('bold') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleBold().run()}
          sx={{ minWidth: 40, px: 1, fontWeight: 'bold', borderRadius: 1 }}
          disabled={disabled}
        >
          B
        </Button>
        <Button 
          size="small" 
          variant={editor.isActive('italic') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          sx={{ minWidth: 40, px: 1, fontStyle: 'italic', borderRadius: 1 }}
          disabled={disabled}
        >
          I
        </Button>
        <Button 
          size="small" 
          variant={editor.isActive('underline') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          sx={{ minWidth: 40, px: 1, textDecoration: 'underline', borderRadius: 1 }}
          disabled={disabled}
        >
          U
        </Button>
      </Box>
      <Box sx={{ 
        flex: 1, 
        p: 2, 
        overflow: 'auto',
        backgroundColor: 'white',
        '& .ProseMirror': {
          outline: 'none',
          minHeight: '200px',
          '&:focus': {
            outline: 'none'
          }
        }
      }}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

// 900,000 bytes(약 900KB) 단위로 분할
function splitAnswerContent(str, chunkSize = 900000) {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);
  let chunks = [];
  let offset = 0;
  while (offset < bytes.length) {
    // 바이트 단위로 자르되, 멀티바이트 문자가 깨지지 않게 처리
    let end = offset + chunkSize;
    if (end > bytes.length) end = bytes.length;
    // 디코딩이 깨지지 않게, end를 조정
    let chunk = new TextDecoder().decode(bytes.slice(offset, end));
    // 만약 chunk를 다시 인코딩했을 때 chunkSize보다 크면, 한 글자씩 줄여가며 맞춤
    while (new TextEncoder().encode(chunk).length > chunkSize) {
      chunk = chunk.slice(0, -1);
    }
    chunks.push(chunk);
    offset += new TextEncoder().encode(chunk).length;
  }
  return chunks;
}

const ticketTypeLabels = {
  'MPT_AI_2': 'MPT AI 응시권 (2문제)',
  'MPT_PROFESSOR_2': 'MPT Professor 응시권 (2문제)',
  // 필요시 추가
};

const MptTestMainPage = () => {
  const [sets, setSets] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedSet, setSelectedSet] = useState(null);
  const [answer, setAnswer] = useState('');
  const [timer, setTimer] = useState(TIMER_DURATION);
  const [timerActive, setTimerActive] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timerRef = useRef();
  const [activeTab, setActiveTab] = useState('file');
  const [filePage, setFilePage] = useState(1);
  const [fileNumPages, setFileNumPages] = useState(null);
  const [libraryPage, setLibraryPage] = useState(1);
  const [libraryNumPages, setLibraryNumPages] = useState(null);
  const prevFileUrl = useRef();
  const prevLibraryUrl = useRef();
  const [ticketList, setTicketList] = useState([]);
  const [selectedTicketType, setSelectedTicketType] = useState('');
  const [ticketLoading, setTicketLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  // 고유한 년도 목록 추출
  const years = React.useMemo(() => {
    const uniqueYears = [...new Set(sets.map(set => set.year))];
    return uniqueYears.sort((a, b) => b - a); // 내림차순 정렬
  }, [sets]);

  // 선택된 년도에 해당하는 월 목록 추출
  const months = React.useMemo(() => {
    if (!selectedYear) return [];
    const uniqueMonths = [...new Set(sets
      .filter(set => set.year === selectedYear)
      .map(set => set.month))];
    return uniqueMonths.sort((a, b) => a - b); // 오름차순 정렬
  }, [sets, selectedYear]);

  // 선택된 년도와 월에 해당하는 시험 세트 목록
  const filteredSets = React.useMemo(() => {
    if (!selectedYear || !selectedMonth) return [];
    return sets.filter(set => 
      set.year === selectedYear && 
      set.month === selectedMonth
    );
  }, [sets, selectedYear, selectedMonth]);

  // 년도나 월이 변경될 때 선택된 시험 세트 초기화
  useEffect(() => {
    setSelectedId('');
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    async function fetchSets() {
      const { data, error } = await supabase.from('mpt_paper_sets').select('*').order('year', { ascending: false });
      console.log('supabase data:', data, 'error:', error);
      setSets(data || []);
    }
    fetchSets();
  }, []);

  useEffect(() => {
    setSelectedSet(sets.find(s => s.id === selectedId) || null);
  }, [sets, selectedId]);

  useEffect(() => {
    if (selectedId) {
      setTimer(TIMER_DURATION);
      setTimerActive(true);
      setIsSubmitted(false);
      setAnswer('');
    } else {
      setTimerActive(false);
    }
  }, [selectedId]);

  useEffect(() => {
    if (!timerActive) return;
    if (timer <= 0) {
      setTimerActive(false);
      if (!isSubmitted) {
        handleSubmit();
      }
    }
    timerRef.current = setTimeout(() => setTimer(t => t - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [timer, timerActive]);

  useEffect(() => {
    if (selectedSet?.file_url !== prevFileUrl.current) {
      setFilePage(1);
      setFileNumPages(null);
      prevFileUrl.current = selectedSet?.file_url;
    }
    if (selectedSet?.library_url !== prevLibraryUrl.current) {
      setLibraryPage(1);
      setLibraryNumPages(null);
      prevLibraryUrl.current = selectedSet?.library_url;
    }
    setActiveTab('file');
  }, [selectedSet]);

  // 1. 모든 MPT 응시권 조회
  useEffect(() => {
    async function fetchTickets() {
      setTicketLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        setTicketList([]);
        setSelectedTicketType('');
        setTicketLoading(false);
        return;
      }
      // 1. 응시권
      const { data } = await supabase
        .from('user_exam_tickets')
        .select('type, count, remain')
        .eq('user_id', userId)
        .like('type', 'MPT_%');
      // 2. 스터디권
      const { data: studyTickets } = await supabase
        .from('user_study_tickets')
        .select('type, count')
        .eq('user_id', userId)
        .eq('type', 'STUDY_MPT_1');
      // 3. 통합
      const allTickets = [
        ...(data || []),
        ...(studyTickets || [])
      ];
      setTicketList(allTickets);
      // 자동 선택: 우선순위(응시권 > 스터디권)
      if (allTickets && allTickets.length > 0) {
        setSelectedTicketType(allTickets[0].type);
      } else {
        setSelectedTicketType('');
      }
      setTicketLoading(false);
    }
    fetchTickets();
  }, []);

  // Dialog 핸들러 추가
  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);
  const handleConfirmSubmit = async () => {
    setDialogOpen(false);
    await handleSubmit();
  };

  const handleSubmit = async () => {
    if (!selectedSet || isSubmitted || isSubmitting || !answer.trim()) return;
    
    try {
      setIsSubmitting(true);

      await withAuthRefresh(async (user) => {
        const ticket = ticketList.find(t => t.type === selectedTicketType);
        console.log('selectedTicketType:', selectedTicketType, 'ticket:', ticket);
        
        if (!ticket || ticket.count < 1) {
          throw new Error('선택한 권종이 없습니다. 구매 후 이용해 주세요.');
        }

        await supabase.from('user_exam_tickets')
          .update({ count: ticket.count - 1 })
          .eq('user_id', user.id)
          .eq('type', selectedTicketType);
        
        // mpt_answers에도 답안 저장
        const { error } = await supabase.from('mpt_answers').insert([{
          user_id: user.id,
          answer_content: answer,
          set_id: selectedSet.id,
          subject: selectedSet.subject,
          submission_group_id: uuidv4(),
          submission_time: new Date().toISOString(),
          remaining_time: timer,
          user_email: user.email,
          set_title: selectedSet.title,
          exam_year: selectedSet.year,
          exam_month: selectedSet.month.toString(),
          evaluation_type: selectedTicketType.includes('AI') ? 'ai' : (selectedTicketType.includes('PROFESSOR') ? 'professor' : 'self'),
          is_ai_answer: false,
          is_sample_answer: false,
          is_candidate_answer: true
        }]);
        
        if (error) {
          throw new Error('답안 저장에 실패했습니다: ' + error.message);
        }
        
        alert('답안 제출이 완료되었습니다.');
        setIsSubmitted(true);
      });

    } catch (error) {
      console.error('답안 제출 오류:', error);
      if (error.message?.includes('인증이 필요합니다')) {
        alert('로그인이 만료되었습니다. 페이지를 새로고침하고 다시 로그인해주세요.');
      } else {
        alert('답안 제출 중 오류가 발생했습니다: ' + error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // memoizedFileViewer, memoizedLibraryViewer도 prop 구조에 맞게 수정
  const memoizedFileViewer = React.useMemo(() => (
    <PdfViewer
      url={selectedSet ? selectedSet.file_url : null}
      label="File"
    />
  ), [selectedSet?.file_url]);

  const memoizedLibraryViewer = React.useMemo(() => (
    <PdfViewer
      url={selectedSet ? selectedSet.library_url : null}
      label="Library"
    />
  ), [selectedSet?.library_url]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 3
      }}
    >
      <Container maxWidth="xl">
                {/* 헤더 섹션 */}
        <Fade in timeout={800}>
          <Box sx={{ 
            textAlign: 'center', 
            mb: 6,
            py: 4,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%)',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(255, 255, 255, 0.8)'
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h2" sx={{ 
                fontWeight: 800, 
                mb: 2,
                background: 'linear-gradient(45deg, #1976d2, #9c27b0)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                📝 MPT 실전 테스트
              </Typography>
              <Typography variant="h5" sx={{ 
                mb: 3, 
                fontWeight: 400,
                color: '#424242'
              }}>
                Multistate Performance Test • {sets.length > 0 && `총 ${sets.length}개 세트 제공`}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Chip 
                  icon={<AssignmentIcon />} 
                  label="실전 시뮬레이션" 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontSize: '1rem', py: 1 }}
                />
                <Chip 
                  icon={<EditIcon />} 
                  label="서면 작성 연습" 
                  color="secondary" 
                  variant="outlined"
                  sx={{ fontSize: '1rem', py: 1 }}
                />
                <Chip 
                  icon={<CheckCircleIcon />} 
                  label="전문가 평가" 
                  color="success" 
                  variant="outlined"
                  sx={{ fontSize: '1rem', py: 1 }}
                />
              </Box>
            </Box>
            
            {/* 타이머 */}
            {timerActive && (
              <Box sx={{ 
                position: 'absolute', 
                top: 16, 
                right: 16,
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                px: 3,
                py: 1.5,
                borderRadius: 3,
                background: timer < 600 ? 
                  'linear-gradient(45deg, #f44336 30%, #ff5722 90%)' :
                  timer < 1800 ? 
                  'linear-gradient(45deg, #ff9800 30%, #ffc107 90%)' :
                  'linear-gradient(45deg, #4caf50 30%, #8bc34a 90%)',
                color: 'white',
                boxShadow: 3,
                animation: timer < 300 ? 'pulse 1s infinite' : 'none'
              }}>
                <TimerIcon />
                <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                  {formatTime(timer)}
                </Typography>
              </Box>
            )}

            {/* 진행률 표시 */}
            {selectedSet && (
                              <Box sx={{ mt: 3, maxWidth: 600, mx: 'auto' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: '#424242' }}>
                      선택된 세트: {selectedSet.title}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: '#424242' }}>
                      {selectedSet.year}년 {selectedSet.month}월
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={answer ? 25 : 0} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: 'rgba(25, 118, 210, 0.15)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                        borderRadius: 4
                      }
                    }} 
                  />
                </Box>
            )}
          </Box>
        </Fade>

        {/* 선택 컨트롤 카드 */}
        <Fade in timeout={600}>
          <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="primary" />
                시험 세트 선택
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>년도</InputLabel>
                    <Select
                      value={selectedYear}
                      label="년도"
                      onChange={e => setSelectedYear(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      {years.map(year => (
                        <MenuItem key={year} value={year}>
                          {year}년
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth disabled={!selectedYear}>
                    <InputLabel>월</InputLabel>
                    <Select
                      value={selectedMonth}
                      label="월"
                      onChange={e => setSelectedMonth(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      {months.map(month => (
                        <MenuItem key={month} value={month}>
                          {month}월
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={!selectedYear || !selectedMonth}>
                    <InputLabel>시험 세트</InputLabel>
                    <Select
                      value={selectedId}
                      label="시험 세트"
                      onChange={e => setSelectedId(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      {filteredSets.map(set => (
                        <MenuItem key={set.id} value={set.id}>
                          {set.title} {set.number ? `(${set.number})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* 응시권 선택 */}
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                🎫 응시권 선택
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>권종 선택</InputLabel>
                  <Select
                    value={selectedTicketType}
                    label="권종 선택"
                    onChange={e => setSelectedTicketType(e.target.value)}
                    disabled={ticketList.length === 0}
                    sx={{ borderRadius: 2 }}
                  >
                    {ticketList.map(ticket => (
                      <MenuItem key={ticket.type} value={ticket.type}>
                        {
                          ticket.type.startsWith('MPT_')
                            ? `${ticketTypeLabels[ticket.type] || ticket.type} : ${ticket.count}개${ticket.remain !== undefined ? ` (남은 ${ticket.remain}회)` : ''}`
                            : `셀프 MPT 스터디권 : ${ticket.count}개`
                        }
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* 보유 응시권 Chip 나열 */}
                {ticketList.map(ticket => (
                  <Chip
                    key={ticket.type}
                    icon={<span role="img" aria-label="ticket">🎫</span>}
                    label={
                      ticket.type.startsWith('MPT_')
                        ? `${ticketTypeLabels[ticket.type] || ticket.type} : ${ticket.count}개${ticket.remain !== undefined ? ` (남은 ${ticket.remain}회)` : ''}`
                        : `셀프 MPT 스터디권 : ${ticket.count}개`
                    }
                    color="primary"
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  />
                ))}
                {ticketList.length === 0 && (
                  <Chip
                    icon={<span role="img" aria-label="ticket">🎫</span>}
                    label="보유한 MPT 응시권/스터디권이 없습니다."
                    color="default"
                    sx={{ borderRadius: 2 }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Fade>
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: '6fr 4fr',
        gap: { xs: 2, sm: 3 },
        height: 'calc(100vh - 380px)',
        alignItems: 'stretch',
        mb: 3,
        minWidth: 0,
        minHeight: 0,
      }}>
        {/* PDF 뷰어 영역 */}
        <Card sx={{ borderRadius: 3, boxShadow: 3, overflow: 'visible' }}>
          <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Tabs 
              value={activeTab} 
              onChange={(_, v) => setActiveTab(v)} 
              sx={{ 
                px: 2,
                py: 1,
                borderBottom: 1, 
                borderColor: 'divider',
                flexShrink: 0,
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                '& .MuiTab-root': {
                  minHeight: 40,
                  py: 1,
                  px: 3,
                  borderRadius: 2,
                  mx: 0.5,
                  fontWeight: 'bold',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                },
                '& .MuiTabs-indicator': {
                  display: 'none'
                }
              }}
            >
              <Tab 
                value="file" 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon fontSize="small" />
                    File
                  </Box>
                } 
              />
              <Tab 
                value="library" 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LibraryIcon fontSize="small" />
                    Library
                  </Box>
                } 
              />
            </Tabs>
            <Box sx={{ 
              flex: 1,
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible'
            }}>
              {!selectedSet ? (
                <Box sx={{ 
                  flex: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  minHeight: 400,
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <DescriptionIcon sx={{ fontSize: 64, opacity: 0.3, color: 'primary.main' }} />
                  <Typography variant="h6" color="text.secondary">
                    시험 세트를 선택하세요
                  </Typography>
                </Box>
              ) : (
                <>
                  {activeTab === 'file' && memoizedFileViewer}
                  {activeTab === 'library' && memoizedLibraryViewer}
                </>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* 답안 입력 영역 */}
        <Card sx={{ borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
          <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EditIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                답안 입력
              </Typography>
              {isSubmitted && (
                <Chip 
                  label="제출 완료"
                  color="success"
                  size="small"
                  icon={<CheckCircleIcon />}
                  sx={{ ml: 'auto', fontWeight: 'bold' }}
                />
              )}
            </Box>
            
            <RichTextEditor
              value={answer}
              onChange={setAnswer}
              disabled={!selectedSet || isSubmitted || !selectedTicketType}
              placeholder={isSubmitted ? "평가가 종료되었습니다. 일주일 내에 이메일로 결과를 발송해 드립니다." : selectedSet ? "답안을 입력하세요" : "시험 세트를 먼저 선택하세요"}
            />
            
            <Button
              variant="contained"
              size="large"
              startIcon={<SendIcon />}
              onClick={handleOpenDialog}
              fullWidth
              disabled={
                !selectedSet ||
                isSubmitted ||
                isSubmitting ||
                !answer.trim() ||
                !selectedTicketType ||
                (ticketList.find(t => t.type === selectedTicketType)?.count < 1)
              }
              sx={{ 
                mt: 2,
                py: 1.5,
                borderRadius: 3,
                fontWeight: 'bold',
                fontSize: '1.1rem',
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1976D2 30%, #0288D1 90%)',
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                },
                '&:disabled': {
                  background: 'grey.300'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {!selectedTicketType
                ? '응시권 필요'
                : isSubmitting
                ? '제출 중...'
                : isSubmitted
                ? '제출 완료'
                : '답안 제출'}
            </Button>
            
            {(!selectedTicketType || (ticketList.find(t => t.type === selectedTicketType)?.count < 1)) && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mt: 2,
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
              >
                <Typography variant="body2">
                  MPT 응시권 또는 셀프스터디권이 필요합니다.{' '}
                  <Link
                    component={RouterLink}
                    to="/pricing"
                    sx={{ 
                      color: 'primary.main', 
                      textDecoration: 'underline', 
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    요금제 페이지로 이동
                  </Link>
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* 답안 제출 확인 Dialog 추가 */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: 8,
            minWidth: 340,
            p: 1.5,
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.light',
            color: 'primary.main',
            fontWeight: 700,
            textAlign: 'center',
            fontSize: '1.25rem',
            letterSpacing: 1,
            borderRadius: 2,
            mb: 1
          }}
        >
          답안 제출 확인
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <DialogContentText sx={{ fontSize: '1.1rem', color: 'text.primary', mb: 2 }}>
            {selectedSet && (
              <>
                <span style={{ fontWeight: 700, color: '#1976d2' }}>
                  {selectedSet.year} {selectedSet.month} {selectedSet.subject}
                </span>
                <br />
                {selectedTicketType === 'STUDY_MPT_1' ? (
                  <span style={{ color: '#666', fontSize: '0.98rem' }}>
                    답안을 제출하시겠습니까?<br />
                    셀프 스터디권 1회가 차감됩니다.
                  </span>
                ) : (
                  (() => {
                    const ticket = ticketList.find(t => t.type === selectedTicketType);
                    const remain = ticket?.remain ?? 0;
                    const afterRemain = remain - 1;
                    return (
                      <span style={{ color: '#666', fontSize: '0.98rem' }}>
                        답안을 제출하시겠습니까?<br />
                        MPT 응시권 1장으로 2문제(2세트)까지 제출할 수 있습니다.<br />
                        이번 제출로 남은 {remain}회 중 1회가 차감됩니다.<br />
                        (남은 제출 가능 횟수 {afterRemain}회)
                      </span>
                    );
                  })()
                )}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button 
            onClick={handleCloseDialog} 
            color="inherit" 
            variant="outlined" 
            sx={{ minWidth: 90, borderRadius: 2 }}
          >
            취소
          </Button>
          <Button 
            onClick={handleConfirmSubmit} 
            color="primary" 
            variant="contained" 
            sx={{ minWidth: 90, borderRadius: 2, fontWeight: 700 }}
          >
            제출
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  );
};

export default MptTestMainPage;